import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {formatDate} from '@angular/common';
import {Observable, of} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {StatementResponsesVm, StatementsVm, StatementVm} from '../../models/team-dynamic.vm';

export interface StatementFilters {
  sort: 'ASC' | 'DESC';
  startDate?: string;
  endDate?: string;
}

@Component({
  selector: 'app-indicator-statements',
  templateUrl: './statements.component.html',
  styleUrls: ['./statements.component.scss']
})
export class IndicatorStatementsComponent implements OnInit, OnChanges {
  private static readonly DEFAULT_RANGE_DAYS = 30;

  @Input() data: StatementsVm | null = null;
  @Input() loading: boolean = false;
  @Input() responses: StatementResponsesVm | null = null;

  @Output() filtersChanged = new EventEmitter<StatementFilters>();
  @Output() statementOpened = new EventEmitter<StatementVm>();

  sortOptions: { label: string, value: 'ASC' | 'DESC' }[] = [];
  periodOptions: { label: string, value: string | null }[] = [];
  sortValue: 'ASC' | 'DESC' = 'DESC';
  periodValue: string | null = null;
  periodLabel: string = '';
  showCustomRange: boolean = false;
  rangeStart: Date | null = null;
  rangeEnd: Date | null = null;
  canApplyRange: boolean = false;

  selectedTabs: boolean[] = [];
  openIndex: number | null = null;
  statementCharts: any[] = [];

  chartOptions = {
    plugins: {
      tooltip: {
        enabled: false
      }
    },
    elements: {
      arc: {
        borderColor: '#E6E6E6',
        borderWidth: 3
      }
    }
  };
  responseDistribution: Observable<any> | undefined;
  openResponses: any[] = [];
  responsesCount: number = 0;

  private appliedStartDate?: string;
  private appliedEndDate?: string;

  constructor(private translate: TranslateService) {
  }

  ngOnInit(): void {
    this.setOptions();
    this.setDefaultRange();
    this.setPeriodLabel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.setStatementTabs();
      this.setStatementCharts();
      this.clearResponses();
    }
    if (changes['responses']) {
      this.setResponses();
    }
  }

  onSortChange() {
    this.emitFilters();
  }

  onPeriodChange(event: any) {
    if (event.value === 'custom') {
      this.showCustomRange = true;
      return;
    }
    this.showCustomRange = false;
    this.appliedStartDate = undefined;
    this.appliedEndDate = undefined;
    this.setDefaultRange();
    this.setPeriodLabel();
    this.emitFilters();
  }

  onRangeDateChange() {
    this.canApplyRange = !!this.rangeStart && !!this.rangeEnd && this.rangeEnd >= this.rangeStart;
  }

  applyCustomRange() {
    if (!this.canApplyRange) {
      return;
    }
    this.showCustomRange = false;
    this.appliedStartDate = this.getFormattedDate(this.rangeStart);
    this.appliedEndDate = this.getFormattedDate(this.rangeEnd);
    this.setPeriodLabel();
    this.emitFilters();
  }

  onStatementOpen(statement: StatementVm, index: number) {
    if (this.openIndex !== null && this.openIndex !== index) {
      this.selectedTabs[this.openIndex] = false;
    }
    this.selectedTabs[index] = true;
    this.selectedTabs = [...this.selectedTabs];
    this.openIndex = index;
    this.clearResponses();
    this.statementOpened.emit(statement);
  }

  onStatementClose(index: number) {
    if (this.openIndex !== index) {
      return;
    }
    this.selectedTabs[index] = false;
    this.selectedTabs = [...this.selectedTabs];
    this.openIndex = null;
    this.clearResponses();
  }

  trackByUuid(index: number, statement: StatementVm) {
    return statement.uuid ?? index;
  }

  private emitFilters() {
    this.filtersChanged.emit({
      sort: this.sortValue,
      startDate: this.appliedStartDate,
      endDate: this.appliedEndDate
    });
  }

  private setDefaultRange() {
    const start = new Date();
    start.setDate(start.getDate() - IndicatorStatementsComponent.DEFAULT_RANGE_DAYS);
    this.rangeStart = start;
    this.rangeEnd = new Date();
    this.onRangeDateChange();
  }

  private getFormattedDate(date: Date | null): string | undefined {
    return date ? formatDate(date, 'dd.MM.yyyy', 'en-US') : undefined;
  }

  private setPeriodLabel() {
    this.periodLabel = this.appliedStartDate && this.appliedEndDate
      ? this.appliedStartDate + '/' + this.appliedEndDate
      : this.translate.instant('custom_poll_main_reporting_all_time');
  }

  private setResponses() {
    this.clearResponses();
    if (!this.responses) {
      return;
    }
    if (this.responses.type === 'scaled') {
      this.responseDistribution = of({
        ...this.responses.payload,
        onInsight: true,
        eligibleForResult: true
      });
      return;
    }
    if (this.responses.type === 'open') {
      this.openResponses = this.responses.payload?._embedded?.responses ?? [];
      this.responsesCount = this.responses.totalItems;
    }
  }

  private setStatementTabs() {
    this.selectedTabs = new Array(this.data?.items?.length ?? 0).fill(false);
    this.openIndex = null;
  }

  private setStatementCharts() {
    this.statementCharts = (this.data?.items ?? []).map(statement => statement.score === null ? null : {
      datasets: [{
        data: [statement.score, 100 - statement.score],
        backgroundColor: [statement.color, '#E6E6E6']
      }]
    });
  }

  private clearResponses() {
    this.responseDistribution = undefined;
    this.openResponses = [];
    this.responsesCount = 0;
  }

  private setOptions() {
    this.sortOptions = [
      {label: this.translate.instant('td_team_overview_highest_score'), value: 'DESC'},
      {label: this.translate.instant('td_team_overview_lowest_score'), value: 'ASC'}
    ];
    this.periodOptions = [
      {label: this.translate.instant('custom_poll_main_reporting_all_time'), value: null},
      {label: this.translate.instant('td_statements_custom_range'), value: 'custom'}
    ];
  }
}
